import { Button, Tooltip, useMinDelayState } from '@ibsys/kit';
import { CheckIcon, CodeIcon, SearchIcon, TriangleAlert, TypeIcon, XIcon } from 'lucide-react';
import { ChangeEvent, FC, KeyboardEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { AiInput } from '@/shared/ui';
import type { SmartSearchInputProps } from './types';

export const SmartSearchInput: FC<SmartSearchInputProps> = ({
  value,
  onChange,
  mode,
  onModeChange,
  onSearch,
  onClear,
  predicateInfo,
  error,
  isLoading = false,
  placeholder,
  className,
  disabled = false,
}) => {
  const { t } = useTranslation(['dialogs', 'common']);

  const isMagic = useMinDelayState(isLoading, 600);

  // Display value based on mode
  const displayValue = mode === 'nl' ? value.human : value.dsl;

  const startSearch = useCallback(() => {
    onSearch?.();
  }, [onSearch, onSearch]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && !isLoading) {
        startSearch();
      }
    },
    [isLoading, startSearch],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (mode === 'nl') {
        onChange({ human: e.target.value || '', dsl: '' });
      } else {
        onChange({ dsl: e.target.value || '', human: '' });
      }
    },
    [mode],
  );

  const toggleMode = useCallback(() => {
    onModeChange(mode === 'nl' ? 'dsl' : 'nl');
  }, [mode, onModeChange]);

  const handleBlur = useCallback(() => {
    const trimmedValue = displayValue.trim();
    if (trimmedValue) {
      startSearch();
    } else {
      onClear();
    }
  }, [displayValue, startSearch, onClear]);

  const handleClear = useCallback(() => {
    onChange({ human: '', dsl: '' });
    onSearch();

    onClear();
  }, [mode, onSearch, onClear, onChange]);

  const issues = predicateInfo?.issues ?? [];
  const hasConversionResult = predicateInfo !== null && predicateInfo !== undefined;

  const getConversionStatus = () => {
    if (!hasConversionResult) {
      return { type: 'none' as const, message: null };
    }

    if (!issues || issues.length === 0) {
      return { type: 'success' as const, message: null };
    }

    const hasConvertationError = issues.some((issue) => issue.type === 'ConvertationError');
    if (hasConvertationError) {
      const errorIssue = issues.find((issue) => issue.type === 'ConvertationError');
      return { type: 'error' as const, message: errorIssue?.message || null };
    }

    const hasSyntaxError = issues.some((issue) => issue.type === 'SyntaxError');
    if (hasSyntaxError) {
      const errorIssue = issues.find((issue) => issue.type === 'SyntaxError');
      return { type: 'error' as const, message: errorIssue?.message || null };
    }

    const firstIssue = issues[0];
    return { type: 'warning' as const, message: firstIssue?.message || null };
  };

  const status = getConversionStatus();

  const renderStatusIcon = () => {
    if (status.type === 'none' || status.type === 'error') {
      return null;
    }

    if (status.type === 'success') {
      return (
        <div className="flex h-5 w-5 items-center justify-center rounded bg-green-500">
          <CheckIcon className="h-3 w-3 text-white" />
        </div>
      );
    }

    if (status.type === 'warning') {
      const icon = (
        <div className="flex h-5 w-5 items-center justify-center rounded bg-yellow-500">
          <TriangleAlert className="h-3 w-3 text-white" />
        </div>
      );

      if (status.message) {
        return (
          <Tooltip
            tooltip={
              <div className="text-sm text-white">
                <span>{status.message}</span>
              </div>
            }
            classNames={{ content: 'bg-yellow-600 p-2' }}
          >
            {icon}
          </Tooltip>
        );
      }

      return icon;
    }

    return null;
  };

  const defaultPlaceholder =
    mode === 'nl' ? t('dialogs:smartSearch.placeholderNl') : t('dialogs:smartSearch.placeholderDsl');

  const iconRight = (
    <div className="flex gap-2 items-center">
      {renderStatusIcon()}
      {displayValue.trim() && (
        <Button
          tabIndex={-1}
          size="sm"
          variant="ghost"
          disabled={isMagic || disabled}
          icon={<XIcon />}
          onClick={handleClear}
          aria-label={t('common:clear')}
        />
      )}
      <Button
        tabIndex={-1}
        size="sm"
        variant="ghost"
        disabled={isMagic || disabled}
        icon={mode === 'nl' ? <CodeIcon /> : <TypeIcon />}
        onClick={toggleMode}
        aria-label={mode === 'nl' ? t('dialogs:smartSearch.switchToDsl') : t('dialogs:smartSearch.switchToNl')}
      />
      <Button
        tabIndex={-1}
        size="sm"
        variant="ghost"
        disabled={isMagic || disabled || !displayValue.trim()}
        icon={<SearchIcon />}
        onClick={onSearch}
        aria-label={t('common:search')}
      />
    </div>
  );

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <AiInput
        iconRight={iconRight}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder ?? defaultPlaceholder}
        isProcessing={isMagic}
        disabled={isLoading || disabled}
        className={cn(mode === 'dsl' && 'font-mono', error && 'border-destructive')}
        classNames={{
          iconRight: 'top-5',
        }}
      />

      {(error || status.type === 'error') && <div className="text-sm text-destructive">{error || status.message}</div>}
    </div>
  );
};
